import React, { useState, useEffect } from 'react';
import { ArrowLeft, Eye, Smartphone, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useExperience } from '../../../stores/Experience/Experience';
import { useAuth } from '../../../stores/Auth/Auth';

const THEME_COLOR = '#c59253';

const ExperienceShell = ({
    currentStep,
    totalSteps,
    plan,
    onBack,
    onNext,
    onSkip,
    children,
    hideActions = false,
    hideFooter = false, // Alias for hideActions but more explicit for our usage
    nextLabel = 'Continue',
    preview = null
}) => {
    const [showMobilePreview, setShowMobilePreview] = useState(false);
    const reset = useExperience(state => state.reset);
    const user = useAuth(state => state.user);
    // Prevent scrolling when mobile preview is open
    useEffect(() => {
        if (showMobilePreview) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => { document.body.style.overflow = 'auto'; };
    }, [showMobilePreview]);

    const planLabels = {
        free: 'Free',
        basic: 'Basic',
        enterprise: 'Enterprise',
    };

    const progress = (currentStep / totalSteps) * 100;
    const shouldShowFooter = !hideActions && !hideFooter;

    return (
        <div className="d-flex flex-column vh-100 bg-white shadow-sm overflow-hidden position-relative">
            {/* Header */}
            <header className="border-bottom bg-white position-relative flex-shrink-0" style={{ zIndex: 100 }}>
                <div className="container-fluid px-4 py-2 py-md-3 pb-0">
                    <div className="d-flex align-items-center justify-content-between mb-2 mb-md-3">
                        <div className="d-flex align-items-center gap-3">
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="btn btn-link p-0 text-decoration-none text-muted d-flex align-items-center gap-2"
                                    style={{ color: '#6c757d' }}
                                >
                                    <ArrowLeft size={20} />
                                    <span className="d-none d-sm-inline">Back</span>
                                </button>
                            )}
                            <div className="d-flex align-items-center">
                                <span className="text-muted small fw-bold">
                                    Step {currentStep} of {totalSteps}
                                </span>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <button
                                onClick={() => {
                                    if (window.confirm('Are you sure you want to exit? Your progress is saved.')) {
                                        reset();
                                        // if the user didnt complete his onboarding then we log him out
                                        if (user?.onboardingCompleted) {
                                            window.location.href = '/';
                                        } else {
                                            window.location.href = '/logout';
                                        }
                                    }
                                }}
                                className="btn btn-sm btn-outline-danger d-flex align-items-center gap-2 rounded-pill px-3 py-1 fw-bold"
                                style={{ fontSize: '12px' }}
                            >
                                <X size={14} />
                                Exit
                            </button>
                        </div>
                    </div>
                </div>

                {/* Progress Bar - sticks to bottom */}
                <div className="progress rounded-0 shadow-sm" style={{ height: '4px' }}>
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{
                            width: `${progress}%`,
                            backgroundColor: THEME_COLOR,
                            transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                        aria-valuenow={progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                    />
                </div>
            </header>

            {/* Main Content - This is the only scrollable area */}
            <main className="flex-grow-1 position-relative bg-light" style={{ overflow: 'hidden' }}>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.02 }}
                        transition={{ duration: 0.3 }}
                        className="h-100 overflow-auto"
                    >
                        {children}
                    </motion.div>
                </AnimatePresence>
            </main>

            {/* Footer Actions */}
            {shouldShowFooter && (
                <footer className="border-top bg-white flex-shrink-0 shadow-lg" style={{ zIndex: 100 }}>
                    <div className="container py-2 py-md-3 px-4">
                        <div className="d-flex align-items-center justify-content-end gap-3">
                            {onSkip && (
                                <button
                                    onClick={onSkip}
                                    className="btn btn-link text-decoration-none text-muted small fw-medium"
                                >
                                    Skip setup
                                </button>
                            )}
                            {onNext && (
                                <button
                                    onClick={onNext}
                                    className="btn text-white px-5 py-2 rounded-pill fw-bold shadow-sm"
                                    style={{
                                        backgroundColor: THEME_COLOR,
                                        borderColor: THEME_COLOR,
                                        minWidth: '140px'
                                    }}
                                >
                                    {nextLabel}
                                </button>
                            )}
                        </div>
                    </div>
                </footer>
            )}

            {/* Mobile Preview Bubble */}
            {preview && !showMobilePreview && (
                <motion.button
                    initial={{ scale: 0, rotate: -45 }}
                    animate={{ scale: 1, rotate: 0 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowMobilePreview(true)}
                    className="btn rounded-circle d-lg-none d-flex align-items-center justify-content-center shadow-lg position-fixed border-0"
                    style={{
                        bottom: '85px',
                        right: '25px',
                        width: '60px',
                        height: '60px',
                        backgroundColor: THEME_COLOR,
                        color: 'white',
                        zIndex: 1000,
                        border: '2px solid white'
                    }}
                >
                    <Smartphone size={28} />
                </motion.button>
            )}

            {/* Full Screen Preview Overlay */}
            <AnimatePresence>
                {showMobilePreview && (
                    <motion.div
                        initial={{ opacity: 0, y: '100%' }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="position-fixed top-0 start-0 w-100 h-100 bg-white"
                        style={{ zIndex: 2000 }}
                    >
                        {/* Overlay Header */}
                        <div className="bg-white border-bottom p-3 d-flex align-items-center justify-content-between">
                            <h6 className="mb-0 fw-bold">Mobile Preview</h6>
                            <button
                                onClick={() => setShowMobilePreview(false)}
                                className="btn btn-dark rounded-circle d-flex p-0 justify-content-center align-items-center text-white"
                                style={{ width: '36px', height: '36px' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Preview Content */}
                        <div className="h-100 overflow-auto pb-5">
                            {preview}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ExperienceShell;
